import { useEffect, useRef, useState } from "react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { exportToExcel, parseWorkbook } from "../lib/excel";
import { toast } from "sonner";
import {
  LayoutDashboard, PlusCircle, Users, Megaphone, FileText,
  Receipt, BookOpen, PieChart, CalendarClock, LogOut, Download, Upload,
} from "lucide-react";
import { cn } from "../lib/utils";
import { LeanWordmark } from "../components/Logo";

import Overview from "../components/tabs/Overview";
import AddEntry from "../components/tabs/AddEntry";
import Clients from "../components/tabs/Clients";
import ActiveClients from "../components/tabs/ActiveClients";
import Pipeline from "../components/tabs/Pipeline";
import Invoices from "../components/tabs/Invoices";
import Expenses from "../components/tabs/Expenses";
import Ledger from "../components/tabs/Ledger";
import ProfitLoss from "../components/tabs/ProfitLoss";

const TABS = [
  { id: "overview", label: "Dashboard", icon: LayoutDashboard },
  { id: "add", label: "Add Entry", icon: PlusCircle },
  { id: "clients", label: "Clients", icon: Users },
  { id: "active", label: "Active", icon: CalendarClock },
  { id: "pipeline", label: "Pipeline", icon: Megaphone },
  { id: "invoices", label: "Invoices", icon: FileText },
  { id: "expenses", label: "Expenses", icon: Receipt },
  { id: "ledger", label: "Ledger", icon: BookOpen },
  { id: "pnl", label: "P&L", icon: PieChart },
];

export default function Dashboard() {
  const data = useData();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [prefillClient, setPrefillClient] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    data.load().catch(() => toast.error("Could not load your data"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goClient = (clientId) => { setPrefillClient(clientId); setTab("add"); };

  const handleExport = () => {
    exportToExcel(data);
    toast.success("Excel downloaded — your portable backup");
  };
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!window.confirm("Importing replaces everything currently in the portal with this file's contents. Continue?")) { e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const parsed = parseWorkbook(evt.target.result);
        await data.replaceAll(parsed);
        toast.success(`Imported ${parsed.entries.length} entries and ${parsed.clients.length} clients`);
      } catch (err) {
        toast.error("Could not read that file — make sure it was exported from this tracker");
      }
      e.target.value = "";
    };
    reader.readAsArrayBuffer(file);
  };

  if (!data.loaded) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <div className="h-10 w-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const Active = { overview: Overview, add: AddEntry, clients: Clients, active: ActiveClients, pipeline: Pipeline, invoices: Invoices, expenses: Expenses, ledger: Ledger, pnl: ProfitLoss }[tab];

  return (
    <div className="min-h-screen bg-background pb-10">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <LeanWordmark className="h-7 sm:h-8 w-auto" />
          <span className="text-[11px] font-semibold text-muted-foreground border-l border-slate-200 pl-3 hidden sm:inline">Staff Workspace</span>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={handleExport} data-testid="excel-export-button" className="hidden sm:inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
              <Download className="h-4 w-4" /> Export
            </button>
            <button onClick={() => fileRef.current?.click()} data-testid="excel-import-button" className="hidden sm:inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
              <Upload className="h-4 w-4" /> Import
            </button>
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={handleImport} data-testid="import-file-input" />
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-slate-200" referrerPolicy="no-referrer" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-slate-200 grid place-items-center text-xs font-bold text-slate-600">{(user?.name || "U")[0]}</div>
            )}
            <button onClick={logout} data-testid="logout-button" title="Log out" className="inline-grid place-items-center h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-rose-600">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        {/* Tab bar */}
        <div className="max-w-6xl mx-auto px-2 sm:px-6">
          <div className="flex gap-1 overflow-x-auto no-scrollbar pb-2">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`nav-${t.id}-tab`}
                  onClick={() => { setTab(t.id); if (t.id !== "add") setPrefillClient(null); }}
                  className={cn(
                    "inline-flex items-center gap-2 whitespace-nowrap rounded-xl px-3.5 py-2 text-sm font-semibold transition-colors",
                    active ? "bg-primary text-primary-foreground shadow-sm shadow-primary/20" : "text-slate-600 hover:bg-slate-100"
                  )}
                >
                  <Icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 animate-fade-up">
        <Active goClient={goClient} prefillClient={prefillClient} onExport={handleExport} onImport={() => fileRef.current?.click()} setTab={setTab} />
      </main>
    </div>
  );
}
